import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { OrderStateType, ApiGetDataStatus } from '@redux/types'
import {
	Order,
	OrderType,
	OrderCreationType,
	OrderUpdateType,
	OrderDeletionType,
	SeasonType,
} from '@schemas'
import dayjs from 'dayjs'
import { readBoFilter } from '@utils/BackofficeFilter'

const today = dayjs().format('YYYY-MM-DD')

/**
 * 지난번에 고른 기준 · 시즌 · 상태 (`utils/BackofficeFilter`). 없으면 `null` 이고 기본값으로 간다.
 * 이 슬라이스는 persist 대상이 아니라 새로고침마다 initialState 로 돌아온다 — 기억은 여기서만 한다.
 */
const savedFilter = readBoFilter('order')

const initialState: OrderStateType = {
	// trigger
	triggerInitOrderPage: false,
	triggerOrderSearch: false,
	triggerOrderPreparing: false,
	triggerOrderLabelPrinting: false,
	triggerUpdateOrderTransit: false,

	// search order — 기준 · 시즌 · 상태는 기억하고, 기간과 검색어는 매번 기본값이다
	searchMode: (savedFilter?.mode as 'date' | 'season' | undefined) ?? 'date',
	searchSeasonId: savedFilter?.seasonId ?? '',
	seasonOptions: [],
	searchStartDate: dayjs(today).format('YYYY-MM-01'),
	searchEndDate: dayjs(today).endOf('month').format('YYYY-MM-DD'),
	searchStatus: savedFilter?.status ?? 'new',
	searchChannel: savedFilter?.channel ?? 'all',
	searchKeyword: '',

	// view
	viewTabIndex: 0,
	detailModalOrderId: '',
	detailOrder: null,
	detailOrderStatus: 'init',
	acting: false,
	refundLockReason: '',

	// order list
	orderStatus: 'init',
	orderList: [],
	selectedOrder: new Order(),

	loading: false,
	error: '',
	selectedYear: dayjs().format('YYYY-MM'),
	isDeletedIncluding: '1',

	// selection
	selectedOrderIds: [],
	selectedStatusArr: [],
}

const orderSlice = createSlice({
	name: 'order',
	initialState,
	reducers: {
		// trigger
		TRIGGER_INIT_ORDER_PAGE: (state: OrderStateType, action: PayloadAction<boolean>) => {
			state.loading = true
			state.triggerInitOrderPage = action.payload
		},
		TRIGGER_ORDER_SEARCH: (state: OrderStateType, action: PayloadAction<boolean>) => {
			state.triggerOrderSearch = action.payload
		},
		TRIGGER_ORDER_PREPARING: (state: OrderStateType, action: PayloadAction<boolean>) => {
			state.triggerOrderPreparing = action.payload
		},
		TRIGGER_ORDER_LABEL_PRINTING: (
			state: OrderStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerOrderLabelPrinting = action.payload
		},
		TRIGGER_UPDATE_ORDER_TRANSIT: (
			state: OrderStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerUpdateOrderTransit = action.payload
		},

		// search order
		SEARCH_MODE: (state: OrderStateType, action: PayloadAction<'date' | 'season'>) => {
			state.searchMode = action.payload
		},
		SEARCH_SEASON_ID: (state: OrderStateType, action: PayloadAction<string>) => {
			state.searchSeasonId = action.payload
		},
		SEASON_OPTIONS: (state: OrderStateType, action: PayloadAction<SeasonType[]>) => {
			state.seasonOptions = action.payload
		},
		SEARCH_START_DATE: (state: OrderStateType, action: PayloadAction<string>) => {
			state.searchStartDate = action.payload
		},
		SEARCH_END_DATE: (state: OrderStateType, action: PayloadAction<string>) => {
			state.searchEndDate = action.payload
		},
		SEARCH_STATUS: (state: OrderStateType, action: PayloadAction<string>) => {
			state.searchStatus = action.payload
		},
		SEARCH_CHANNEL: (state: OrderStateType, action: PayloadAction<string>) => {
			state.searchChannel = action.payload
		},
		SEARCH_KEYWORD: (state: OrderStateType, action: PayloadAction<string>) => {
			state.searchKeyword = action.payload
		},

		// view
		VIEW_TAB_INDEX: (state: OrderStateType, action: PayloadAction<number>) => {
			state.viewTabIndex = action.payload
		},
		/**
		 * 자세히 모달 — 주문 ID 를 주면 열리고 '' 이면 닫힌다.
		 * 정본은 URL 이다(`/main/order/:orderId`). 이 값은 Order.page 가 URL 에서 옮겨 적는다.
		 */
		DETAIL_MODAL_ORDER_ID: (state: OrderStateType, action: PayloadAction<string>) => {
			if (state.detailModalOrderId !== action.payload) {
				// 다른 주문으로 옮겨가면 앞 주문의 딥링크 사본은 버린다
				state.detailOrder = null
				state.detailOrderStatus = 'init'
			}
			state.detailModalOrderId = action.payload
		},
		/** 딥링크 — 목록에 없는 주문 하나를 서버에서 읽는다. 사가가 받는다 */
		FETCH_ORDER_ONE: (state: OrderStateType, action: PayloadAction<string>) => {
			state.detailOrderStatus = 'loading'
		},
		DETAIL_ORDER: (state: OrderStateType, action: PayloadAction<OrderType | null>) => {
			state.detailOrder = action.payload
		},
		DETAIL_ORDER_STATUS: (
			state: OrderStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.detailOrderStatus = action.payload
		},
		/** 주문확인 — PUT /order/:id/prepare (PREPARED_AT 이 찍히면 배송 준비중). 사가가 받는다 */
		TRIGGER_PREPARE_ORDER: (state: OrderStateType, action: PayloadAction<string>) => {
			state.acting = true
		},
		/** 입금 확인 한 건(자세히 모달) — PUT /order/:id/paid. 사가가 받는다 */
		TRIGGER_MARK_PAID: (
			state: OrderStateType,
			action: PayloadAction<{ orderId: string; amount: number | null; ref: string }>,
		) => {
			state.acting = true
		},
		/** 입금 확인 — 선택한 주문 전부(그리드 헤더, 월말 몰아서). 사가가 받는다 */
		TRIGGER_ORDER_MARK_PAID: (state: OrderStateType, action: PayloadAction<boolean>) => {
			state.acting = action.payload
		},
		/** 환불 — `POST /order/:id/refund`. 사유는 필수, overrideLock 은 잠금을 넘길 때만. 사가가 받는다 */
		TRIGGER_REFUND_ONE: (
			state: OrderStateType,
			action: PayloadAction<{ orderId: string; reason: string; overrideLock?: boolean }>,
		) => {
			state.acting = true
		},
		/** 환불 잠금(생산 시작) 사유. '' 이 아니면 모달이 "그래도 환불" 을 묻는다 */
		REFUND_LOCK_REASON: (state: OrderStateType, action: PayloadAction<string>) => {
			state.refundLockReason = action.payload
		},
		ACTING: (state: OrderStateType, action: PayloadAction<boolean>) => {
			state.acting = action.payload
		},

		// order list
		ORDER_STATUS: (state, action: PayloadAction<ApiGetDataStatus>) => {
			state.orderStatus = action.payload
		},
		ORDER_LIST: (state, action: PayloadAction<OrderType[]>) => {
			state.orderList = action.payload
		},
		SELECTED_ORDER: (state, action: PayloadAction<OrderType>) => {
			state.selectedOrder = action.payload
		},
		// 주문 조회 설정 관련 (년도, 삭제 여부)
		IS_DELETED_INCLUDING: (state, action: PayloadAction<'0' | '1'>) => {
			state.isDeletedIncluding = action.payload
		},
		SET_SELECTED_YEAR: (state, action: PayloadAction<string>) => {
			state.selectedYear = action.payload
		},
		// 주문 등록 관련
		REGISTER_ORDER: (state, action: PayloadAction<OrderCreationType>) => {
			state.loading = true
		},
		REGISTER_ORDER_SUCCESS: state => {
			state.loading = false
		},
		REGISTER_ORDER_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.loading = false
		},
		// 주문 수정 관련
		UPDATE_ORDER: (
			state,
			action: PayloadAction<{
				orderId: string
				order: OrderUpdateType
			}>,
		) => {
			state.loading = true
		},
		UPDATE_ORDER_SUCCESS: state => {
			state.loading = false
		},
		UPDATE_ORDER_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.loading = false
		},
		// 주문 삭제 관련
		DELETE_ORDER: (state, action: PayloadAction<OrderDeletionType>) => {
			state.loading = true
		},
		DELETE_ORDER_SUCCESS: state => {
			state.loading = false
		},
		DELETE_ORDER_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.loading = false
		},

		// select
		SELECTED_ORDER_IDS: (state: OrderStateType, action: PayloadAction<string[]>) => {
			state.selectedOrderIds = action.payload
		},
		SELECTED_STATUS_ARR: (state: OrderStateType, action: PayloadAction<string[]>) => {
			state.selectedStatusArr = action.payload
		},
	},
})

export const orderActions = orderSlice.actions
export default orderSlice.reducer
